import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ShoppingCart } from 'lucide-react';
import api from '../utils/axiosConfig';

function formatPrice(amount, currency = 'usd') {
  if (amount == null) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

const Storefront = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/stripe/products');
        setProducts(res.data?.products || []);
      } catch (err) {
        setError(err.response?.data?.error || t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [t]);

  const handleCheckout = async (product) => {
    try {
      setCheckoutLoading(product.id);
      const res = await api.post('/api/stripe/checkout/session', {
        priceId: product.priceId,
        quantity: 1,
        mode: product.connectedAccountId ? 'payment' : 'subscription',
        connectedAccountId: product.connectedAccountId || undefined,
        applicationFeeAmount: product.connectedAccountId ? Math.round((product.amount || 0) * 0.1) : undefined,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto p-6'>
      <h1 className='text-2xl font-bold mb-6'>{t('store.title')}</h1>

      {error && (
        <div className='mb-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm'>
          {error}
        </div>
      )}

      {products.length === 0 ? (
        <p className='text-muted-foreground'>{t('store.noProducts')}</p>
      ) : (
        <div className='grid gap-4 md:grid-cols-2'>
          {products.map((product) => (
            <Card key={product.id}>
              <CardHeader>
                <CardTitle>{product.name}</CardTitle>
                {product.description && (
                  <CardDescription>{product.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className='flex items-center justify-between'>
                  <span className='text-lg font-semibold'>
                    {formatPrice(product.amount, product.currency)}
                  </span>
                  <Button
                    onClick={() => handleCheckout(product)}
                    disabled={!!checkoutLoading}
                  >
                    {checkoutLoading === product.id ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <>
                        <ShoppingCart className='h-4 w-4 mr-2' />
                        {t('store.buy')}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Storefront;
